/**
 * Every admin route, resolved in the browser.
 *
 * The 22 routes with a changing part in the URL — a fixture id, a venue code —
 * are the reason this app cannot be a Next static export: that build writes one
 * HTML file per page and would need to know every id in advance. Here the
 * bucket serves index.html for anything it does not recognise and this table
 * decides what to render, so a fixture created a minute ago works without a
 * redeploy.
 *
 * Everything but /login sits under `RequireSignIn`, which sends the person to
 * /login only when omnium needs a sign-in and has none.
 */

import { createBrowserRouter, Navigate } from "react-router-dom";

import { AdminLayout } from "@/spa/AdminLayout";
import { RequireSignIn } from "@/spa/RequireSignIn";
import { DesignPage } from "@/spa/pages/DesignPage";
import { FlowPage } from "@/spa/pages/FlowPage";
import { LoginPage } from "@/spa/pages/LoginPage";
import { ValidatePage } from "@/spa/pages/ValidatePage";
import { EditRolePage } from "@/spa/pages/EditRolePage";
import { NewRolePage } from "@/spa/pages/NewRolePage";
import { RolesPage } from "@/spa/pages/RolesPage";
import { CommonsPage } from "@/spa/pages/CommonsPage";
import { DashboardPage } from "@/spa/pages/DashboardPage";
import { DeliveryClientsPage } from "@/spa/pages/DeliveryClientsPage";
import { DeliveryClientPage } from "@/spa/pages/DeliveryClientPage";
import { DeliveryStatusPage } from "@/spa/pages/DeliveryStatusPage";
import { PackagesPage } from "@/spa/pages/PackagesPage";
import { EventConsolePage } from "@/spa/pages/EventConsolePage";
import { EventDetailPage } from "@/spa/pages/EventDetailPage";
import { EventsPage } from "@/spa/pages/EventsPage";
import { FixtureDetailPage } from "@/spa/pages/FixtureDetailPage";
import { EditFixturePage } from "@/spa/pages/EditFixturePage";
import { CompetitionsPage } from "@/spa/pages/CompetitionsPage";
import { EditRecordPage } from "@/spa/pages/EditRecordPage";
import { FixturesPage } from "@/spa/pages/FixturesPage";
import { ImportRecordsPage } from "@/spa/pages/ImportRecordsPage";
import { MasterListPage } from "@/spa/pages/MasterListPage";
import { NewRecordPage } from "@/spa/pages/NewRecordPage";
import { NewFixturePage } from "@/spa/pages/NewFixturePage";
import { NewSportPage } from "@/spa/pages/NewSportPage";
import { ScoringBuilderPage } from "@/spa/pages/ScoringBuilderPage";
import { ScoringConsolePage } from "@/spa/pages/ScoringConsolePage";
import { ScoringProgramsPage } from "@/spa/pages/ScoringProgramsPage";
import { ScoringUisPage } from "@/spa/pages/ScoringUisPage";
import { StatsTabPage } from "@/spa/pages/StatsTabPage";
import { AddStatPage } from "@/spa/pages/AddStatPage";
import { StatsRunsPage } from "@/spa/pages/StatsRunsPage";
import { StatsNumbersPage } from "@/spa/pages/StatsNumbersPage";
import { StatsEngineCataloguePage } from "@/spa/pages/StatsCataloguePage";
import { FixtureStatsPage } from "@/spa/pages/FixtureStatsPage";
import { SportFixturesPage } from "@/spa/pages/SportFixturesPage";
import { SportOverviewPage } from "@/spa/pages/SportOverviewPage";
import { SportSetupPage } from "@/spa/pages/SportSetupPage";
import { SportsPage } from "@/spa/pages/SportsPage";
import { TodayPage } from "@/spa/pages/TodayPage";
import { WorkflowFlowPage } from "@/spa/pages/WorkflowFlowPage";
import { WorkflowsPage } from "@/spa/pages/WorkflowsPage";
import { IntegrationPage } from "@/spa/pages/integrations/IntegrationPage";
import { IntegrationsListPage } from "@/spa/pages/integrations/IntegrationsListPage";
import { NewIntegrationPage } from "@/spa/pages/integrations/NewIntegrationPage";

export const router = createBrowserRouter([
  {
    element: <RequireSignIn />,
    children: [
      {
        path: "/",
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/sports" replace /> },

          { path: "sports", element: <SportsPage /> },
          { path: "dashboard", element: <DashboardPage /> },
          { path: "today", element: <TodayPage /> },
          { path: "commons", element: <CommonsPage /> },
          { path: "fixtures", element: <FixturesPage /> },
          { path: "fixtures/:id", element: <FixtureDetailPage /> },
          { path: "events", element: <EventsPage /> },
          { path: "events/:code", element: <EventDetailPage /> },
          { path: "workflows", element: <WorkflowsPage /> },
          // A workflow opens in its flow diagram, full window, like a sport's
          // scoring program. Its apps are chosen on its card on /workflows.
          { path: "workflows/:code", element: <WorkflowFlowPage /> },
          // The old "Apps and commands" page is gone: commands are on the diagram,
          // apps are chosen on the workflow's card. Old links land there.
          {
            path: "workflows/:code/apps",
            element: <Navigate to="/workflows" replace />,
          },
          // Scout data coming in: a Scout job connected to a workflow's import.
          { path: "integrations", element: <IntegrationsListPage /> },
          { path: "integrations/new", element: <NewIntegrationPage /> },
          { path: "integrations/:code", element: <IntegrationPage /> },
          {
            path: "integrations/:code/runs/:runId",
            element: <IntegrationPage />,
          },
          { path: "integrations/:code/:tab", element: <IntegrationPage /> },

          { path: "sports/new", element: <NewSportPage /> },
          { path: "roles", element: <RolesPage /> },
          { path: "roles/new", element: <NewRolePage /> },
          { path: "roles/:id/edit", element: <EditRolePage /> },
          // Outbound delivery control plane: clients, packages, assignments,
          // per-feed transforms. Listed before `/:sport` so they are not
          // swallowed by the sport-scoped catch-all below.
          { path: "delivery", element: <DeliveryClientsPage /> },
          { path: "delivery/packages", element: <PackagesPage /> },
          { path: "delivery/status", element: <DeliveryStatusPage /> },
          { path: "delivery/clients/:id", element: <DeliveryClientPage /> },
          { path: "validate", element: <ValidatePage /> },
          { path: "flow", element: <FlowPage /> },
          { path: "design", element: <DesignPage /> },

          // Sport-scoped. Listed after the fixed paths so `/sports` is never
          // swallowed by `/:sport`.
          { path: ":sport", element: <SportOverviewPage /> },
          { path: ":sport/setup", element: <SportSetupPage /> },
          { path: ":sport/competitions", element: <CompetitionsPage /> },
          { path: ":sport/fixtures", element: <SportFixturesPage /> },
          { path: ":sport/fixtures/new", element: <NewFixturePage /> },
          { path: ":sport/fixtures/:id/edit", element: <EditFixturePage /> },
          { path: ":sport/masters/:master", element: <MasterListPage /> },
          { path: ":sport/masters/:master/new", element: <NewRecordPage /> },
          {
            path: ":sport/masters/:master/import",
            element: <ImportRecordsPage />,
          },
          {
            path: ":sport/masters/:master/:code/edit",
            element: <EditRecordPage />,
          },
          { path: ":sport/scoring", element: <ScoringProgramsPage /> },
          { path: ":sport/scoring/uis", element: <ScoringUisPage /> },

          // The stats engine. `/stats` and `/stats-engine` are the same screen:
          // the old catalogue/runs/numbers pages were deleted with the old engine.
          { path: ":sport/stats", element: <StatsTabPage /> },
          { path: ":sport/stats-engine", element: <StatsTabPage /> },
          { path: ":sport/stats-engine/runs", element: <StatsRunsPage /> },
          {
            path: ":sport/stats-engine/numbers",
            element: <StatsNumbersPage />,
          },
          { path: ":sport/stats-engine/add", element: <AddStatPage /> },
          {
            path: ":sport/stats-engine/catalogue",
            element: <StatsEngineCataloguePage />,
          },
          {
            path: ":sport/stats-engine/fixture/:fixtureId",
            element: <FixtureStatsPage />,
          },
        ],
      },

      // Full-window routes: no sidebar, no content column.
      { path: "/:sport/scoring/:id", element: <ScoringBuilderPage /> },
      { path: "/scoring-console/:id", element: <ScoringConsolePage /> },
      { path: "/events/:code/console", element: <EventConsolePage /> },
    ],
  },

  { path: "/login", element: <LoginPage /> },
]);
